import { AlertTriangle, RefreshCw, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWebhookSync } from "@/hooks/use-webhook-sync";
import { useWebhookStatus } from "@/hooks/use-webhook-status";
import { useAuthStore } from "@/stores/auth.store";
import { Button } from "@/components/ui/button";

// ═══════════════════════════════════════════════════════════
// WebhookEventsBanner — App-wide admin alert when the tenant's
// B24 webhook events are out of sync with what the active rules
// actually require.
//
// Only visible to admins. Reads LIVE status (event.get via
// /api/admin/webhooks/status) rather than the D1 cache — the cache
// only reflects the last confirmed-good sync, and if a sync attempt
// ever fails partway, trusting it would mask exactly the kind of
// silent drift this banner exists to catch.
// Hourly cron also auto-fixes this; banner confirms it resolved.
// ═══════════════════════════════════════════════════════════

export function WebhookEventsBanner() {
  const { t } = useTranslation("common");
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const { data: status } = useWebhookStatus({ enabled: isAdmin });
  const webhookSync = useWebhookSync();

  const outOfSync = !!status && !status.inSync;

  // Only show for admins with loaded status when out of sync
  if (!isAdmin || !status) return null;
  if (!outOfSync) return null;

  const handleSync = () => {
    webhookSync.mutate();
  };

  return (
    <div className="flex flex-col gap-1 border-b border-amber-500/30 bg-amber-500/10 px-6 py-3 sm:flex-row sm:items-center sm:gap-3">
      <div className="flex items-center gap-2 shrink-0">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
        <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">
          {t("webhookBanner.title")}
        </span>
      </div>
      <p className="flex-1 text-sm text-amber-600 dark:text-amber-500">{t("webhookBanner.desc")}</p>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          variant="outline"
          className="border-amber-500/50 text-amber-700 hover:bg-amber-500/20 dark:text-amber-400 dark:hover:bg-amber-500/20"
          disabled={webhookSync.isPending}
          onClick={handleSync}
          aria-label={t("webhookBanner.syncNow")}
        >
          <RefreshCw
            className={`mr-1.5 h-3.5 w-3.5 ${webhookSync.isPending ? "animate-spin" : ""}`}
            aria-hidden="true"
          />
          {webhookSync.isPending ? t("webhookBanner.syncing") : t("webhookBanner.syncNow")}
        </Button>
        <Button size="sm" variant="ghost" className="text-amber-700 dark:text-amber-400" asChild>
          <Link to="/settings">
            <Settings className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t("webhookBanner.viewSettings")}
          </Link>
        </Button>
      </div>
    </div>
  );
}
